import { useMutation } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { toCoPaymentWarning, type CoPaymentWarning } from './co-payment-warning';

/**
 * POST /api/v1/Practice/PostBookCalculatePrice
 *
 * Recalculates the price breakdown after the appointment is already
 * booked — fires when the user switches sponsor type on the view-appointment
 * popup (Self-pay / Insurance / Corporate).
 *
 * Mirrors Flutter `postBookCalculatePrice` in
 * `scheduling_data_source_impl.dart:776-808`, called from
 * `payment_widget.dart` `SetPaymentTypeState` / `SelectInsuranceState` /
 * `SelectCorporateAgreementState` handlers.
 *
 * Wire-shape contract (dev-curl verified 2026-05-13):
 *   {
 *     patientId, invoiceId, providerId,
 *     paymentType: 'Self-pay' | 'Insurance' | 'Corporate',
 *     sponsorId?: <guid>,         // OMIT for Self-pay (matches Flutter)
 *     individualServices: []      // empty from the popup
 *   }
 *
 * Response is the recalculated price breakdown — same shape as
 * AppointmentInvoiceModel pricing fields.
 */

export interface PostBookCalculatePriceRequest {
  patientId: string;
  invoiceId: string;
  providerId: string;
  paymentType: 'Self-pay' | 'Insurance' | 'Corporate';
  /** Omit (undefined) for Self-pay per Flutter parity. Insurance/Corporate
   *  ship the selected payer's id (insuranceProvider.id /
   *  corporateProvider.id). */
  sponsorId?: string;
  /**
   * The FULL desired service list (not a delta) — each `{ id: serviceId,
   * quantity, type }`. The billing tab sends the whole list on every
   * add/remove/qty edit; the backend recomputes + persists the invoice's
   * service lines (curl-verified 2026-06-04). Empty `[]` from the
   * view-appointment popup (pure sponsor recalc).
   */
  individualServices?: Array<{ id?: string; quantity?: number; type?: string }>;
}

/**
 * Raw wire shape (dev-curl verified 2026-07-30): the endpoint returns the same
 * short keys as PreBook / CalculateSponsorAmount — `gross / discount / net /
 * patientAmount / sponsorAmount / payable` — NOT the `grossTotal / netTotal`
 * long keys. Consumers that read `grossTotal` off the raw response got
 * `undefined` (Self-pay switch showed AED 0.00). We normalise below so callers
 * get the invoice-style keys they expect.
 */
interface RawPostBookCalculatePriceResponse {
  gross?: number;
  discount?: number;
  net?: number;
  patientAmount?: number;
  sponsorAmount?: number;
  payable?: number;
  currency?: string;
  coPaymentWarning?: unknown;
}

export interface PostBookCalculatePriceResponse {
  grossTotal?: number;
  discountTotal?: number;
  netTotal?: number;
  patientPayable?: number;
  /** Sponsor / insurance portion (Flutter `sponsorOutstanding ← sponsorAmount`). */
  sponsorOutstanding?: number;
  /** Raw passthrough — the view-appointment popup reads `sponsorAmount` directly. */
  sponsorAmount?: number;
  insuranceAmount?: number;
  patientShare?: number;
  currency?: string;
  /** PROD-3347: why the price has no patient co-payment; null when there is nothing to say. */
  coPaymentWarning: CoPaymentWarning | null;
}

export function usePostBookCalculatePrice() {
  return useMutation<
    PostBookCalculatePriceResponse,
    Error,
    PostBookCalculatePriceRequest
  >({
    mutationFn: async (params) => {
      // Build the body via explicit-key assembly so the `sponsorId` key
      // is GUARANTEED ABSENT from the serialised JSON when paymentType is
      // Self-pay. Per Flutter parity (payment_widget.dart:124-148) the
      // Self-pay branch builds the request without `sponsorId` at all —
      // not `sponsorId: null` or `sponsorId: undefined` (the latter
      // gets dropped by JSON.stringify but the former does not, and
      // .NET model binders treat them differently for Guid fields).
      // We only ADD the key when both conditions hold; we never start
      // with it and conditionally delete.
      const body: Record<string, unknown> = {
        patientId: params.patientId,
        invoiceId: params.invoiceId,
        providerId: params.providerId,
        paymentType: params.paymentType,
        individualServices: params.individualServices ?? [],
      };
      if (params.paymentType !== 'Self-pay' && params.sponsorId) {
        body.sponsorId = params.sponsorId;
      }
      const raw = await post<RawPostBookCalculatePriceResponse>(
        '/api/v1/Practice/PostBookCalculatePrice',
        body,
      );
      // Normalise raw short keys → invoice-style keys (mirrors
      // `useCalculatePrice`). Keep `sponsorAmount` as a raw passthrough for the
      // popup, which reads it directly.
      return {
        grossTotal: raw.gross,
        discountTotal: raw.discount,
        netTotal: raw.net,
        patientPayable: raw.payable,
        sponsorOutstanding: raw.sponsorAmount,
        sponsorAmount: raw.sponsorAmount,
        currency: raw.currency,
        coPaymentWarning: toCoPaymentWarning(raw.coPaymentWarning),
      };
    },
  });
}
